/* Browser ▸ Canva — the desktop's Canva account picker (CanvaAccountPicker) and Canva designs (Connect API),
 * in the Brightivity kit. A web page cannot embed canva.com, so designs come through brightivity.dev/canva. */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, EmptyState, Icon, Modal, SearchInput, Segmented, Select, Skeleton, Spinner, Tag } from '../../ui/index.jsx';
import { exportDesign, linkStart, listAccounts, listDesigns, removeAccount, signOutAccount, useAccount } from '../../api/canva.js';
import { makeThumbnail, saveDesign } from '../../api/library.js';
import { usePrintStore } from '../print/PrintStore.jsx';
import './browser.css';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n/index.js';

/** The user's Canva accounts (shared with StudioCore: the same links show in both apps). */
export function useCanvaAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const load = useCallback(async () => {
    const r = await listAccounts();
    if (r.ok) { setAccounts(r.data.accounts || []); setError(null); } else { setAccounts([]); setError(r.error); setOff(r.status === 503); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { accounts, error, off, reload: load };
}

const initial = (a) => ((a.displayName || a.email || 'C').trim()[0] || 'C').toUpperCase();
const nameOf = (a) => a.displayName || a.email || i18n.t('bv:canva.account');

/** Start Canva sign-in: Brightivity → Canva → back to this page with ?canva=linked. */
export async function addCanvaAccount(returnPath = '/browser') {
  const r = await linkStart(`${window.location.origin}${returnPath}`);
  if (!r.ok) return r.error || i18n.t('bv:canva.startFailed');
  window.location.assign(r.data.authUrl);
  return null;
}

export function CanvaAccountPicker({ accounts, onOpen, onChanged, returnPath = '/browser', compact }) {
  const { t } = useTranslation('bv');
  const [confirm, setConfirm] = useState(null); // {kind, account}
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [hint, setHint] = useState(false);
  const add = async () => { setBusy('add'); setErr(await addCanvaAccount(returnPath)); setBusy(null); };
  const act = async () => {
    const { kind, account } = confirm; setConfirm(null); setBusy(account.id);
    const r = kind === 'remove' ? await removeAccount(account.id) : await signOutAccount(account.id);
    setBusy(null);
    if (!r.ok) setErr(r.error); else onChanged && onChanged();
  };
  return (
    <div className={`bv-canva-accounts${compact ? ' is-compact' : ''}`} data-testid="canva-accounts">
      <div className="bv-canva-grid">
        {(accounts || []).map((a) => (
          <Card key={a.id} className="bv-canva-card" data-testid="canva-account">
            <button type="button" className="bv-canva-card__main" onClick={() => (a.signedIn ? onOpen && onOpen(a) : add())} disabled={busy === a.id}>
              <span className="bv-canva-avatar">{a.avatarUrl ? <img src={a.avatarUrl} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.remove(); }} /> : initial(a)}</span>
              <span className="bv-canva-card__text">
                <b>{nameOf(a)}</b>
                {a.email && a.displayName && <span className="bv-muted">{a.email}</span>}
                <Tag color={a.signedIn ? 'green' : 'yellow'}>{a.signedIn ? t('canva.signedIn') : t('canva.signedOut')}</Tag>
              </span>
            </button>
            <div className="bv-canva-card__actions">
              {a.signedIn && <button type="button" className="bv-linkbtn" onClick={() => setConfirm({ kind: 'sign-out', account: a })}>{t('shell.signOut')}</button>}
              <button type="button" className="bv-linkbtn" onClick={() => setConfirm({ kind: 'remove', account: a })}>{t('common.remove')}</button>
            </div>
          </Card>
        ))}
        <button type="button" className="bv-canva-add" onClick={add} disabled={busy === 'add'} data-testid="canva-add">
          {busy === 'add' ? <Spinner label={t('canva.opening')} /> : <Icon name="plus" size={28} />}
          <b>{accounts && accounts.length ? t('canva.addAnother') : t('canva.signInWith')}</b>
          <span className="bv-muted">{t('canva.readsOnly')}</span>
        </button>
      </div>
      <p className="bv-small bv-muted">
        {t('canva.linksCurrent')}{' '}
        <button type="button" className="bv-linkbtn" onClick={() => setHint(true)}>{t('canva.secondQ')}</button>
      </p>
      {err && <p className="bv-error-text" role="alert">{err}</p>}
      <Modal open={!!confirm} title={confirm && confirm.kind === 'remove' ? t('canva.removeQ') : t('canva.signOutQ')} onClose={() => setConfirm(null)}>
        <p className="bv-muted">{confirm && (confirm.kind === 'remove'
          ? t('canva.removeBody', { name: nameOf(confirm.account) })
          : t('canva.signOutBody', { name: nameOf(confirm.account) }))}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="coral" onClick={act}>{confirm && confirm.kind === 'remove' ? t('common.remove') : t('shell.signOut')}</Button>
        </div>
      </Modal>
      <Modal open={hint} title={t('canva.hint.title')} onClose={() => setHint(false)}>
        <ol className="bv-canva-steps">
          <li><Trans t={t} i18nKey="canva.hint.s1" components={{ b: <b /> }} /></li>
          <li><Trans t={t} i18nKey="canva.hint.s2" components={{ b: <b /> }} /></li>
          <li>{t('canva.hint.s3')}</li>
        </ol>
        <div className="bv-modal-actions"><Button variant="blue" onClick={() => setHint(false)}>{t('common.gotIt')}</Button></div>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------------ designs */
const SORTS = [{ value: 'relevance', label: 'canva.sort.relevance' }, { value: 'modified_descending', label: 'canva.sort.recent' }, { value: 'modified_ascending', label: 'canva.sort.oldest' }, { value: 'title_ascending', label: 'canva.sort.titleAz' }, { value: 'title_descending', label: 'canva.sort.titleZa' }];
const OWNERSHIP = [{ value: 'any', label: 'common.all' }, { value: 'owned', label: 'canva.own.mine' }, { value: 'shared', label: 'canva.own.shared' }];

export function CanvaDesigns({ account, onSwitch, onRelink }) {
  const { t } = useTranslation('bv');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [ownership, setOwnership] = useState('any');
  const [designs, setDesigns] = useState(null);
  const [cont, setCont] = useState(null);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [bulk, setBulk] = useState(null);
  const print = usePrintStore();
  const navigate = useNavigate();
  useEffect(() => { const t = setTimeout(() => setQ(query.trim()), 350); return () => clearTimeout(t); }, [query]);
  const load = useCallback(async (append) => {
    if (!append) { setDesigns(null); setErr(null); }
    setMore(!!append);
    const r = await listDesigns({ account: account.id, continuation: append ? cont : null, query: q, ownership, sortBy: q ? sortBy : (sortBy === 'relevance' ? 'modified_descending' : sortBy) });
    setMore(false);
    if (!r.ok) { if (r.data && r.data.needsRelink) onRelink && onRelink(account); setErr(r.error); if (!append) setDesigns([]); return; }
    setDesigns((d) => (append ? [...(d || []), ...r.data.designs] : r.data.designs));
    setCont(r.data.continuation);
  }, [account.id, q, ownership, sortBy, cont]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); useAccount(account.id); }, [account.id, q, ownership, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const printSelected = async () => {
    const list = (designs || []).filter((d) => sel.has(d.id));
    const files = [];
    for (let i = 0; i < list.length; i++) {
      setBulk(t('canva.exportingN', { n: i + 1, total: list.length, title: list[i].title }));
      const r = await exportDesign(account.id, list[i].id, 'png');
      if (!r.ok) { setBulk(null); setErr(`${list[i].title}: ${r.error}`); return; }
      r.files.forEach((b, n) => files.push(new File([b], `${list[i].title}${r.files.length > 1 ? ` — ${t('library.pageOfLower', { n: n + 1, total: r.files.length })}` : ''}.png`, { type: b.type || 'image/png' })));
    }
    setBulk(null);
    print.addFiles(files);
    navigate('/print');
  };

  return (
    <div className="bv-canva-designs" data-testid="canva-designs">
      <div className="bv-canva-bar">
        <button type="button" className="bv-canva-pill" onClick={onSwitch} title={t('canva.switchTitle')} data-testid="canva-switch">
          <span className="bv-canva-avatar is-sm">{initial(account)}</span>
          <span>{nameOf(account)}</span>
          <span className="bv-muted">{t('canva.switch')}</span>
        </button>
        <SearchInput placeholder={t('canva.searchPlaceholder')} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={t('canva.searchAria')} className="bv-canva-search" />
        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} options={SORTS.map((s) => ({ ...s, label: t(s.label) }))} aria-label={t('canva.sortAria')} className="bv-canva-sort" />
        <Segmented value={ownership} onChange={setOwnership} options={OWNERSHIP.map((o) => ({ ...o, label: t(o.label) }))} />
        <Button size="sm" icon={selecting ? 'x' : 'check'} onClick={() => { setSelecting((s) => !s); setSel(new Set()); }}>{selecting ? t('common.cancel') : t('common.select')}</Button>
      </div>
      {selecting && (
        <div className="bv-canva-bulk">
          <span>{sel.size ? t('common.nSelected', { count: sel.size }) : t('canva.pickTogether')}</span>
          {bulk ? <span className="bv-muted"><Spinner label={t('canva.exporting')} /> {bulk}</span>
            : <Button variant="coral" icon="print" disabled={!sel.size} onClick={printSelected} data-testid="canva-print-selected">{t('canva.printSelected')}</Button>}
        </div>
      )}
      {err && <p className="bv-error-text" role="alert">{err}</p>}
      {!designs ? (
        <div className="bv-canva-designgrid">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={220} radius={20} />)}</div>
      ) : !designs.length ? (
        <Card><EmptyState icon="canva" title={q ? t('canva.noMatch') : t('canva.none')}>{q ? t('common.tryAnotherWord') : t('canva.noneBody')}</EmptyState></Card>
      ) : (
        <div className="bv-canva-designgrid">
          {designs.map((d) => (
            <button type="button" key={d.id} className={`bv-canva-design${sel.has(d.id) ? ' is-selected' : ''}`} data-testid="canva-design"
              onClick={() => (selecting ? toggle(d.id) : setOpen(d))}>
              <span className="bv-canva-design__thumb">{d.thumbnail ? <img src={d.thumbnail} alt="" loading="lazy" /> : <Icon name="image" size={36} />}</span>
              <span className="bv-canva-design__name">{d.title}</span>
              <span className="bv-canva-design__meta">{d.pageCount ? t('canva.pages', { count: d.pageCount }) : ''}{d.updatedAt ? ` · ${new Date(d.updatedAt * 1000).toLocaleDateString(i18n.language)}` : ''}</span>
              {selecting && <span className="bv-canva-design__tick">{sel.has(d.id) ? <Icon name="check" size={16} /> : null}</span>}
            </button>
          ))}
        </div>
      )}
      {cont && <div className="bv-canva-more"><Button onClick={() => load(true)} disabled={more}>{more ? t('common.loadingDots') : t('common.loadMore')}</Button></div>}
      {open && <CanvaDesignModal account={account} design={open} onClose={() => setOpen(null)} onRelink={onRelink} />}
    </div>
  );
}

function CanvaDesignModal({ account, design, onClose, onRelink }) {
  const { t } = useTranslation('bv');
  const [format, setFormat] = useState('png');
  const pages = design.pageCount || 1;
  const [pick, setPick] = useState(() => new Set(Array.from({ length: Math.min(pages, 200) }, (_, i) => i + 1)));
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const print = usePrintStore();
  const navigate = useNavigate();
  const chosen = [...pick].sort((a, b) => a - b);
  const run = async (what) => {
    setBusy(what); setErr(null); setMsg(null);
    const fmt = what === 'print' ? 'png' : what === 'save' ? 'png' : format;
    const r = await exportDesign(account.id, design.id, fmt, chosen.length === pages ? null : chosen, (i) => setMsg(t('canva.exportingSecs', { s: i * 2 + 2 })));
    setMsg(null);
    if (!r.ok) { setBusy(null); setErr(r.error); if (r.needsRelink) onRelink && onRelink(account); return; }
    const name = (n) => `${design.title}${r.files.length > 1 ? ` — ${t('library.pageOfLower', { n: chosen[n] || n + 1, total: pages })}` : ''}`;
    if (what === 'print') {
      print.addFiles(r.files.map((b, n) => new File([b], `${name(n)}.png`, { type: b.type || 'image/png' })));
      navigate('/print');
      return;
    }
    if (what === 'save') {
      let saved = 0;
      for (let n = 0; n < r.files.length; n++) {
        const f = new File([r.files[n]], `${name(n)}.png`, { type: 'image/png' });
        const t = await makeThumbnail(f);
        const s = await saveDesign({ name: name(n), source: 'canva', width: t.width, height: t.height, metadata: { url: 'canva', platform: 'Canva', canvaDesignId: design.id, canvaAccountId: account.id, page: chosen[n] || n + 1, pages } }, { file: f, thumbnail: t.file, ...t.previews });
        if (!s.ok) { setBusy(null); setErr(s.error); return; }
        saved++;
      }
      setBusy(null); setMsg(t('canva.savedN', { count: saved }));
      return;
    }
    r.files.forEach((b, n) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b); a.download = `${name(n)}.${fmt === 'jpg' ? 'jpg' : fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    });
    setBusy(null);
  };
  return (
    <Modal open title={design.title} onClose={onClose} width={860}>
      <div className="bv-canva-detail" data-testid="canva-detail">
        <div className="bv-canva-detail__pic">{design.thumbnail ? <img src={design.thumbnail} alt={design.title} /> : <Icon name="image" size={60} />}</div>
        <div className="bv-canva-detail__side">
          <p className="bv-small bv-muted">{t('canva.pages', { count: pages })} · {t('canva.fromAccount', { name: nameOf(account) })}</p>
          {pages > 1 && (
            <div>
              <div className="bv-label">{t('canva.pagesLabel')}</div>
              <div className="bv-canva-pages">
                {Array.from({ length: Math.min(pages, 200) }, (_, i) => i + 1).map((n) => (
                  <button type="button" key={n} className={pick.has(n) ? 'is-on' : ''} aria-pressed={pick.has(n)}
                    onClick={() => setPick((s) => { const x = new Set(s); if (x.has(n)) { if (x.size > 1) x.delete(n); } else x.add(n); return x; })}>{n}</button>
                ))}
              </div>
              <button type="button" className="bv-linkbtn" onClick={() => setPick(new Set(Array.from({ length: Math.min(pages, 200) }, (_, i) => i + 1)))}>{t('canva.allPages')}</button>
            </div>
          )}
          <Button variant="coral" icon="print" block disabled={!!busy} onClick={() => run('print')} data-testid="canva-print">{busy === 'print' ? t('common.preparing') : t('print.print')}</Button>
          <Button variant="blue" icon="library" block disabled={!!busy} onClick={() => run('save')} data-testid="canva-save">{busy === 'save' ? t('common.saving') : t('common.saveToLibrary')}</Button>
          <div className="bv-canva-dl">
            <Select value={format} onChange={(e) => setFormat(e.target.value)} aria-label={t('canva.format')} options={[{ value: 'png', label: 'PNG' }, { value: 'pdf', label: 'PDF' }, { value: 'jpg', label: 'JPG' }]} />
            <Button icon="down" disabled={!!busy} onClick={() => run('download')}>{busy === 'download' ? t('canva.exportingDots') : t('common.download')}</Button>
          </div>
          {design.editUrl && <a className="bv-btn bv-btn--secondary bv-btn--block" href={design.editUrl} target="_blank" rel="noreferrer"><Icon name="design" size={20} />{t('canva.openIn')}</a>}
          {msg && <p className="bv-small" role="status">{busy ? <Spinner label={t('canva.exporting')} /> : null} {msg}</p>}
          {err && <p className="bv-error-text" role="alert">{err}</p>}
        </div>
      </div>
    </Modal>
  );
}
